import { useState, useEffect } from 'react'
import { formatDateTime } from '@shared/utils/datetime'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AssignmentInd as AssignIcon
} from '@mui/icons-material'
import { ticketService } from '../../../../shared/services/TicketService'
import { Ticket, TicketStatus } from '../../../../shared/types/entities'
import { useAuth } from '@shared/auth/AuthContext'
import { TicketDetailModal } from '../../components/ticket/TicketDetailModal'

function AgentQueue() {
  const { user } = useAuth()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'All'>('All')
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const loadTickets = async () => {
    try {
      setLoading(true)
      setError(null)
      const filters = {
        status: statusFilter === 'All' ? undefined : statusFilter,
        search: searchQuery || undefined
      }
      const response = await ticketService.getAllTickets(1, 100, filters)
      setTickets(response.data)
    } catch (err: any) {
      setError(err.message || 'Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTickets()
  }, [statusFilter])

  const handleSearch = () => {
    loadTickets()
  }

  const handleAssignToMe = async (ticketId: string) => {
    if (!user?.userId) {
      setError('User not authenticated')
      return
    }

    try {
      await ticketService.assignTicket(ticketId, user.userId)
      loadTickets()
    } catch (err: any) {
      setError(err.message || 'Failed to assign ticket')
    }
  }

  const getStatusColor = (status: TicketStatus): 'default' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'Open':
        return 'warning'
      case 'InProgress':
        return 'info'
      case 'OnHold':
        return 'warning'
      case 'WaitingOnThirdParty':
        return 'warning'
      case 'Resolved':
        return 'success'
      case 'Closed':
        return 'default'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'Urgent':
        return '#d32f2f'
      case 'High':
        return '#f57c00'
      case 'Medium':
        return '#fbc02d'
      case 'Low':
        return '#388e3c'
      default:
        return '#757575'
    }
  }

  const formatDate = (dateString: string): string => {
    return formatDateTime(dateString, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Ticket Queue
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadTickets} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ flex: 1 }}
            />
            <Tabs
              value={statusFilter}
              onChange={(_, newValue) => setStatusFilter(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="All" value="All" />
              <Tab label="Open" value="Open" />
              <Tab label="In Progress" value="InProgress" />
              <Tab label="On Hold" value="OnHold" />
              <Tab label="Waiting on Third Party" value="WaitingOnThirdParty" />
              <Tab label="Resolved" value="Resolved" />
              <Tab label="Closed" value="Closed" />
            </Tabs>
          </Stack>
        </CardContent>
      </Card>

      {/* Ticket Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : tickets.length === 0 ? (
        <Alert severity="info">No tickets found</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ticket #</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requester</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow
                  key={ticket.ticketId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedTicketId(ticket.ticketId)
                    setModalOpen(true)
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {ticket.ticketNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ticket.subject}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.priority}
                      size="small"
                      sx={{
                        backgroundColor: getPriorityColor(ticket.priority),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {ticket.categoryName ? (
                      <Chip
                        label={ticket.categoryName}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#9c27b0', color: '#9c27b0' }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.status}
                      size="small"
                      color={getStatusColor(ticket.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ticket.requesterName || 'Unknown'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(ticket.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {!ticket.assignedAgentId && (
                      <Tooltip title="Assign to me">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAssignToMe(ticket.ticketId)
                          }}
                        >
                          <AssignIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        open={modalOpen}
        ticketId={selectedTicketId}
        onClose={() => {
          setModalOpen(false)
          setSelectedTicketId(null)
        }}
        onUpdate={() => {
          // Refresh ticket list when ticket is updated
          loadTickets()
        }}
      />
    </Box>
  )
}

export default AgentQueue